import { useState, useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { toast } from 'sonner';
import { Mail, Send } from 'lucide-react';
import SEO from '@/components/SEO';
import Footer from '@/components/Footer';

import { Breadcrumbs } from '@/components/Breadcrumbs';

// Remplacez cette valeur par votre clé site Turnstile (visible publiquement)
const TURNSTILE_SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY || '';

declare global {
  interface Window {
    turnstile?: {
      render: (container: HTMLElement, options: Record<string, unknown>) => string;
      remove: (widgetIdOrContainer: HTMLElement | string) => void;
      reset: (widgetIdOrContainer: HTMLElement | string) => void;
    };
    onTurnstileLoad?: () => void;
  }
}

const contactSchema = z.object({
  name: z.string()
    .trim()
    .min(1, { message: "Le nom est requis" })
    .max(100, { message: "Le nom ne peut pas dépasser 100 caractères" }),
  email: z.string()
    .trim()
    .email({ message: "Email invalide" })
    .max(255, { message: "L'email ne peut pas dépasser 255 caractères" }),
  subject: z.string()
    .trim()
    .min(1, { message: "Le sujet est requis" })
    .max(200, { message: "Le sujet ne peut pas dépasser 200 caractères" }),
  message: z.string()
    .trim()
    .min(10, { message: "Le message doit contenir au moins 10 caractères" })
    .max(2000, { message: "Le message ne peut pas dépasser 2000 caractères" }),
});

type ContactFormValues = z.infer<typeof contactSchema>;

const Contact = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState('');
  const [turnstileLoaded, setTurnstileLoaded] = useState(false);
  const turnstileRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string>('');
  const loadTriggeredRef = useRef(false);
  const formContainerRef = useRef<HTMLDivElement>(null);

  const form = useForm<ContactFormValues>({
    resolver: zodResolver(contactSchema),
    defaultValues: {
      name: '',
      email: '',
      subject: '',
      message: '',
    },
  });

  // Deferred loader: triggered by first form interaction or when the form enters the viewport.
  useEffect(() => {
    if (!TURNSTILE_SITE_KEY) return;

    const triggerLoad = () => {
      if (loadTriggeredRef.current) return;
      loadTriggeredRef.current = true;
      setTurnstileLoaded(true);
    };

    const container = formContainerRef.current;
    const onFocusIn = () => triggerLoad();
    container?.addEventListener('focusin', onFocusIn, { once: true });

    let observer: IntersectionObserver | null = null;
    if (container && 'IntersectionObserver' in window) {
      observer = new IntersectionObserver((entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          triggerLoad();
          observer?.disconnect();
        }
      }, { rootMargin: '200px' });
      observer.observe(container);
    } else {
      triggerLoad();
    }

    return () => {
      container?.removeEventListener('focusin', onFocusIn);
      observer?.disconnect();
    };
  }, []);

  // Actual Turnstile script injection + widget render, only once load is triggered.
  useEffect(() => {
    if (!TURNSTILE_SITE_KEY || !turnstileLoaded) return;

    const renderWidget = () => {
      if (window.turnstile && turnstileRef.current && !widgetIdRef.current) {
        widgetIdRef.current = window.turnstile.render(turnstileRef.current, {
          sitekey: TURNSTILE_SITE_KEY,
          callback: (token: string) => setTurnstileToken(token),
          'expired-callback': () => setTurnstileToken(''),
          'error-callback': () => setTurnstileToken(''),
        });
      }
    };

    if (!document.getElementById('turnstile-script')) {
      const script = document.createElement('script');
      script.id = 'turnstile-script';
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
      script.async = true;
      script.defer = true;
      script.onload = renderWidget;
      document.body.appendChild(script);
    } else if (window.turnstile) {
      renderWidget();
    } else {
      window.onTurnstileLoad = renderWidget;
    }

    return () => {
      if (window.turnstile && turnstileRef.current && widgetIdRef.current) {
        window.turnstile.remove(turnstileRef.current);
        widgetIdRef.current = '';
      }
    };
  }, [turnstileLoaded]);

  const onSubmit = async (values: ContactFormValues) => {
    if (TURNSTILE_SITE_KEY && !turnstileToken) {
      toast.error('Veuillez compléter la vérification de sécurité.');
      return;
    }

    setIsSubmitting(true);
    try {
      const { error } = await supabase.functions.invoke('send-contact-email', {
        body: { ...values, turnstileToken },
      });

      if (error) throw error;

      toast.success('Message envoyé avec succès ! Nous vous répondrons rapidement.');
      form.reset();
      setTurnstileToken('');
      if (window.turnstile && turnstileRef.current) {
        window.turnstile.reset(turnstileRef.current);
      }
    } catch (error) {
      toast.error('Erreur lors de l\'envoi du message. Veuillez réessayer.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <SEO
        title="Contact | Collectif Ibbü"
        description="Contactez le Collectif Ibbü pour toute question concernant l'action collective des conseillers iAdvize/Ibbu."
        canonical="https://collectif-ibbu.org/contact"
      />
      <div className="min-h-screen bg-background">



        {/* Header with Breadcrumbs */}
        <div className="border-b bg-muted/30">
          <div className="max-w-4xl mx-auto px-4 py-6">
            <Breadcrumbs items={[{ label: 'Contact' }]} />
          </div>
        </div>

        {/* Page Header with H1 */}
        <div className="max-w-4xl mx-auto px-4 pt-12 pb-6">
          <div className="text-center space-y-4">
            <h1 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-primary via-primary/80 to-primary bg-clip-text text-transparent">
              Nous contacter
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Vous avez des questions ? N'hésitez pas à nous contacter, nous vous répondrons dans les plus brefs délais.
            </p>
          </div>
        </div>

        <main className="max-w-4xl mx-auto px-4 pb-12">
          <Card>
            <CardContent>
              <div ref={formContainerRef}>
              <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                  <FormField
                    control={form.control}
                    name="name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Nom complet</FormLabel>
                        <FormControl>
                          <Input placeholder="Jean Dupont" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Email</FormLabel>
                        <FormControl>
                          <Input type="email" placeholder="jean.dupont@example.com" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="subject"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Sujet</FormLabel>
                        <FormControl>
                          <Input placeholder="Sujet de votre message" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="message"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Message</FormLabel>
                        <FormControl>
                          <Textarea
                            placeholder="Votre message..."
                            className="min-h-[200px]"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {TURNSTILE_SITE_KEY && (
                    <div
                      className="flex justify-center items-center"
                      style={{ minHeight: '65px' }}
                      aria-label="Vérification de sécurité"
                    >
                      <div
                        ref={turnstileRef}
                        style={{ width: '300px', height: '65px' }}
                      />
                    </div>
                  )}

                  <Button
                    type="submit"
                    className="w-full"
                    disabled={isSubmitting || (TURNSTILE_SITE_KEY ? !turnstileToken : false)}
                    size="lg"
                  >
                    <Send className="mr-2 h-4 w-4" />
                    {isSubmitting ? 'Envoi en cours...' : 'Envoyer le message'}
                  </Button>
                </form>
              </Form>
              </div>
            </CardContent>
          </Card>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default Contact;
